import React, { useCallback, useEffect, useRef, useState } from 'react';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

interface CardMediaCarouselProps {
    /** First slide is the photo; the rest are map / weather etc. */
    slides: React.ReactNode[];
    /** Persistent overlays (club badge, sport badge) drawn above every slide. */
    overlay?: React.ReactNode;
    ariaLabel?: string;
}

const prefersReducedMotion = () =>
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

function CardMediaCarousel({ slides, overlay, ariaLabel }: CardMediaCarouselProps) {
    const trackRef = useRef<HTMLDivElement>(null);
    const [index, setIndex] = useState(0);
    const rafRef = useRef<number>();

    const scrollToIndex = useCallback((next: number) => {
        const track = trackRef.current;
        if (!track) return;
        const clamped = Math.max(0, Math.min(slides.length - 1, next));
        track.scrollTo({ left: track.clientWidth * clamped, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
        setIndex(clamped);
    }, [slides.length]);

    const handleScroll = () => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(() => {
            const track = trackRef.current;
            if (!track) return;
            const next = Math.round(track.scrollLeft / track.clientWidth);
            setIndex((prev) => (prev === next ? prev : next));
        });
    };

    useEffect(() => () => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
    }, []);

    return (
        <Box
            sx={{
                position: 'relative',
                aspectRatio: '16 / 9',
                overflow: 'hidden',
                bgcolor: '#0d120c',
                '&:hover .carousel-arrow': { opacity: 1 },
            }}
            role="group"
            aria-roledescription="carousel"
            aria-label={ariaLabel}
            onKeyDown={(e) => {
                if (e.key === 'ArrowRight') { e.preventDefault(); scrollToIndex(index + 1); }
                if (e.key === 'ArrowLeft') { e.preventDefault(); scrollToIndex(index - 1); }
            }}
        >
            <Box
                ref={trackRef}
                onScroll={handleScroll}
                sx={{
                    display: 'flex',
                    height: '100%',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    scrollSnapType: 'x mandatory',
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                }}
            >
                {slides.map((slide, i) => (
                    <Box
                        key={i}
                        sx={{
                            flex: '0 0 100%',
                            width: '100%',
                            height: '100%',
                            scrollSnapAlign: 'start',
                            position: 'relative',
                            overflow: 'hidden',
                            '& .leaflet-container': { touchAction: 'auto' },
                        }}
                    >
                        {slide}
                    </Box>
                ))}
            </Box>

            {overlay}

            <Box
                sx={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 8,
                    zIndex: 600,
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '6px',
                }}
            >
                {slides.map((_, i) => (
                    <Box
                        key={i}
                        component="button"
                        type="button"
                        aria-label={`Slide ${i + 1}`}
                        aria-current={i === index}
                        onClick={() => scrollToIndex(i)}
                        sx={{
                            p: 0,
                            border: 0,
                            cursor: 'pointer',
                            height: 6,
                            width: i === index ? 16 : 6,
                            borderRadius: 999,
                            bgcolor: i === index ? '#fff' : 'rgba(255,255,255,0.55)',
                            transition: 'width .2s ease, background .2s ease',
                        }}
                    />
                ))}
            </Box>

            {index > 0 && (
                <IconButton
                    aria-label="Previous"
                    size="small"
                    className="carousel-arrow"
                    onClick={() => scrollToIndex(index - 1)}
                    sx={arrowSx('left')}
                >
                    <ChevronLeftIcon fontSize="small" />
                </IconButton>
            )}
            {index < slides.length - 1 && (
                <IconButton
                    aria-label="Next"
                    size="small"
                    className="carousel-arrow"
                    onClick={() => scrollToIndex(index + 1)}
                    sx={arrowSx('right')}
                >
                    <ChevronRightIcon fontSize="small" />
                </IconButton>
            )}
        </Box>
    );
}

const arrowSx = (side: 'left' | 'right') => ({
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    [side]: 8,
    zIndex: 600,
    width: 30,
    height: 30,
    color: '#fff',
    bgcolor: 'rgba(12, 18, 12, 0.5)',
    backdropFilter: 'blur(4px)',
    opacity: { xs: 1, md: 0 },
    transition: 'opacity .18s ease',
    '&:hover': { bgcolor: 'rgba(12, 18, 12, 0.7)' },
    '&:focus-visible': { opacity: 1 },
});

export default CardMediaCarousel;
